import type { PropsWithChildren } from 'react';
import { createContext, useContext, useMemo } from 'react';

import { CategoryPageSkeleton } from '@jane/bloom/components/skeletonLoaders/categoryPageSkeleton';
import { FeaturedPageSkeleton } from '@jane/bloom/components/skeletonLoaders/featuredPageSkeleton';
import { useCurrentPage } from '@jane/bloom/hooks/navigation/useCurrentPage';
import { useIsNavigatingTo } from '@jane/bloom/hooks/navigation/useIsNavigating';
import { useStoreSwitcher } from '@jane/bloom/hooks/storeSwitcher/useStoreSwitcher';
import {
  categoryPages,
  featuredPages,
} from '@jane/bloom/utils/routes/routeIds';

const SkeletonLoaderContext = createContext<{
  shouldShowFullSkeletonLoader: boolean;
  skeletonLoader: JSX.Element | null;
}>({
  shouldShowFullSkeletonLoader: false,
  skeletonLoader: null,
});

export function SkeletonLoaderProvider({ children }: PropsWithChildren) {
  const { pinning: isStoreChanging } = useStoreSwitcher();
  const currentPage = useCurrentPage();

  const isCurrentlyFeatured = featuredPages.includes(currentPage.id);
  const isCurrentlyCategory = categoryPages.includes(currentPage.id);

  const isLoadingFeatured = useIsNavigatingTo(/\/featured$/);
  const isLoadingCategory = useIsNavigatingTo(
    /\/(edible|extract|flower|gear|grow|merch|pre-roll|tincture|topical|vape|deals|shop-all)$/
  );

  let skeletonLoader = null;
  /** Loaders when first arriving to a menu page before the store is known */
  if (isStoreChanging) {
    if (isCurrentlyFeatured) {
      skeletonLoader = <FeaturedPageSkeleton />;
    } else if (isCurrentlyCategory) {
      skeletonLoader = <CategoryPageSkeleton />;
    }
  } else {
    /** Loaders when arriving to a menu from any other page, generally after Remix has loaded */
    if (isLoadingFeatured) {
      skeletonLoader = <FeaturedPageSkeleton />;
    } else if (isLoadingCategory) {
      skeletonLoader = <CategoryPageSkeleton />;
    }
  }

  const returnValue = useMemo(
    () => ({
      shouldShowFullSkeletonLoader: isStoreChanging,
      skeletonLoader,
    }),
    [isStoreChanging, skeletonLoader]
  );

  return (
    <SkeletonLoaderContext.Provider value={returnValue}>
      {children}
    </SkeletonLoaderContext.Provider>
  );
}

export const useSkeletonLoader = () => useContext(SkeletonLoaderContext);
